import { WorkOrderWorkStepFlowStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { T, useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useMemo, useState } from "react";
import { useAvailableSteps } from "../hooks/use-available-steps";
import type {
  FrontendStepType,
  PartialFlowStep,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../index";

const NONE_STATION_VALUE = "__none__";

const WorkOrderWorkStepInspector = memo(function WorkOrderWorkStepInspector({
  step,
  setStep,
  onClose,
  steps,
  setHoveredNodeId,
}: StepTypeDetailsProps<PartialWithRequiredType<WorkOrderWorkStepFlowStep>>) {
  const { t } = useTranslation();
  const displayAttributes = step.displayAttributes ?? [];
  const [displayAttrModalOpen, setDisplayAttrModalOpen] = useState(false);

  const nextStep = step.nextId
    ? (steps as PartialFlowStep[] | undefined)?.find(
        (s) => s.id === step.nextId,
      )
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps ?? [], setStep, setHoveredNodeId, t("End"));

  const handleNextStepSelected = createConnectionHandler(0);

  const { data: stationsData } = useRpcQuery(
    RedoOutboundLabelsRpcClientContext,
    { method: "getWorkOrderStations" as const, args: {} },
  );

  const stationOptions = useMemo(() => {
    const options: { id: string; type: "text"; text: string; value: string }[] =
      [
        {
          id: NONE_STATION_VALUE,
          type: "text",
          text: t("None"),
          value: NONE_STATION_VALUE,
        },
      ];
    for (const station of stationsData?.stations ?? []) {
      options.push({
        id: station.id,
        type: "text",
        text: station.name,
        value: station.id,
      });
    }
    return options;
  }, [stationsData, t]);

  function addDisplayAttribute() {
    setStep({
      ...step,
      displayAttributes: [
        ...displayAttributes,
        { label: "", attributeKey: "" },
      ],
    });
  }

  function updateDisplayAttribute(
    index: number,
    field: "label" | "attributeKey",
    value: string,
  ) {
    setStep({
      ...step,
      displayAttributes: displayAttributes.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr,
      ),
    });
  }

  function removeDisplayAttribute(index: number) {
    setStep({
      ...step,
      displayAttributes: displayAttributes.filter((_, i) => i !== index),
    });
  }

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="semibold">
          {t("Work step")}
        </Text>
        {onClose && (
          <RedoButton
            aria-label={t("Close")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        )}
      </Flex>

      <Flex dir="column" gap="md">
        <RedoTextInput
          label={t("Title")}
          setValue={(value) => setStep({ ...step, title: value })}
          value={step.title ?? ""}
        />
        <RedoTextareaInputField
          autoExpand
          label={t("Instructions")}
          setValue={(value) => setStep({ ...step, instructions: value })}
          value={step.instructions ?? ""}
        />
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label={t("Station")}
          options={stationOptions}
          optionSelected={({ item }) =>
            setStep({
              ...step,
              stationId: item.value === NONE_STATION_VALUE ? null : item.value,
            })
          }
          placeholder={t("Select station")}
          selectedItem={
            step.stationId
              ? stationOptions.find((opt) => opt.value === step.stationId)
              : stationOptions[0]
          }
        />
        <RedoCheckbox
          label={t("Print barcode label after completing this step")}
          setValue={(value) =>
            setStep({ ...step, printBarcodeOnComplete: value === true })
          }
          size="sm"
          value={step.printBarcodeOnComplete ?? false}
        />
        <RedoCheckbox
          label={t("Show sibling batches on completion")}
          setValue={(value) =>
            setStep({ ...step, showSiblingBatches: value === true })
          }
          size="sm"
          value={step.showSiblingBatches ?? false}
        />
        <RedoCheckbox
          label={t("Automatically return to queue on handoff")}
          setValue={(value) =>
            setStep({ ...step, autoReturnToQueueOnHandoff: value === true })
          }
          size="sm"
          value={step.autoReturnToQueueOnHandoff ?? false}
        />
      </Flex>

      <Flex dir="column" gap="md">
        <Flex justify="space-between" w="full">
          <Text fontSize="md" fontWeight="semibold">
            {t("Display attributes")}
          </Text>
          <Flex align="center" gap="xs">
            <RedoButton
              hierarchy="tertiary"
              onClick={() => setDisplayAttrModalOpen(true)}
              size="xs"
              text={t("What is this?")}
            />
            <RedoButton
              hierarchy="secondary"
              IconLeading={PlusIcon}
              onClick={addDisplayAttribute}
              size="xs"
              text={t("Add")}
            />
          </Flex>
        </Flex>
        {displayAttributes.map((attr, index) => (
          <Flex align="flex-end" gap="sm" key={index} w="full">
            <RedoTextInput
              fullWidth
              label={t("Label")}
              placeholder={t("e.g. Engraving")}
              setValue={(value) =>
                updateDisplayAttribute(index, "label", value)
              }
              value={attr.label ?? ""}
            />
            <RedoTextInput
              fullWidth
              label={t("Attribute key")}
              placeholder={t("e.g. engraving")}
              setValue={(value) =>
                updateDisplayAttribute(index, "attributeKey", value)
              }
              value={attr.attributeKey ?? ""}
            />
            <RedoButton
              aria-label={t("Remove")}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => removeDisplayAttribute(index)}
              size="sm"
              theme="destructive"
            />
          </Flex>
        ))}
      </Flex>

      <Divider />

      <Flex dir="column" gap="md">
        <Text fontSize="md" fontWeight="semibold">
          {t("Next step")}
        </Text>
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor
          label={t("After this step completes")}
          onOptionMouseEnter={(item) => onItemHover(item.value)}
          onOptionMouseLeave={() => onItemHover(null)}
          options={availableSteps}
          optionSelected={({ item }) => handleNextStepSelected(item.value)}
          placeholder={t("Select next step")}
          selectedItem={
            nextStep
              ? nextStep.type === StepType.DO_NOTHING
                ? availableSteps.find((opt) => opt.value === "unassign")
                : availableSteps.find((opt) => opt.value === nextStep.id)
              : undefined
          }
        />
      </Flex>

      {displayAttrModalOpen && (
        <RedoModal
          hideFooter
          modalSize="sm"
          onModalCloseRequested={() => setDisplayAttrModalOpen(false)}
          title={t("Display attributes")}
        >
          <Flex dir="column" gap="md">
            <Text fontSize="sm">
              {t(
                "Display attributes map line item custom attribute keys to labeled fields shown on the work step. This lets workers see relevant order customizations without leaving the work order.",
              )}
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              {t("How to use")}
            </Text>
            <Text fontSize="sm">
              <T>
                Set a <strong>Label</strong> (e.g. &ldquo;Engraving&rdquo;) and
                the matching <strong>Attribute key</strong> (e.g.{" "}
                &ldquo;engraving&rdquo;). When a work order is processed, the
                value of that line item property will be shown under the label
                you defined.
              </T>
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              {t("Creating line item properties in {{platformName}}", {
                platformName: "Shopify",
              })}
            </Text>
            <Text fontSize="sm">
              {t(
                "Line item properties are added to products via your storefront theme or a {{platformName}} app. The attribute key must exactly match the property name on the line item (case-sensitive). Common sources include product page customization forms and {{platformName}} app integrations that capture personalization data at checkout.",
                { platformName: "Shopify" },
              )}
            </Text>
          </Flex>
        </RedoModal>
      )}
    </Flex>
  );
});

export const workOrderWorkStepType: FrontendStepType<
  PartialWithRequiredType<WorkOrderWorkStepFlowStep>
> = {
  valid: (state) => {
    return !!(state.title && state.title.trim().length > 0);
  },
  type: "custom",
  customInspector: WorkOrderWorkStepInspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextId = targetStepId;
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextId === targetStepId) {
        draft.nextId = undefined;
      }
    });
  },
};
